import { INITIATIVES } from "../../lib/survey-config";
import type { InitiativeTally } from "../../lib/survey-aggregate-api";

const MEDAL_COLORS: Record<string, string> = {
  gold: "#C8A24A",
  silver: "#B8B5AD",
  bronze: "#B07A4A",
  didntLand: "#B25B4C",
};

const OWNER_BY_KEY = new Map(INITIATIVES.map((i) => [i.key, i.owner]));

function emptyTally(key: string, label: string): InitiativeTally {
  return {
    key,
    label,
    isCustom: false,
    involvement: { directlyInvolved: 0, aware: 0, notAware: 0 },
    medals: { gold: 0, silver: 0, bronze: 0, didntLand: 0 },
    rated: 0,
    score: 0,
  };
}

/**
 * Live initiative leaderboard. Always shows all 15 seeded initiatives (zero-filled
 * when no one has rated them yet) plus any respondent-added customs, sorted by score.
 */
export function InitiativeLeaderboard({ tallies }: { tallies: InitiativeTally[] }) {
  const byKey = new Map(tallies.map((t) => [t.key, t]));
  const seeded = INITIATIVES.map((i) => byKey.get(i.key) ?? emptyTally(i.key, i.label));
  const customs = tallies.filter((t) => t.isCustom);
  const all = [...seeded, ...customs].sort((a, b) => b.score - a.score || b.rated - a.rated);

  return (
    <div className="space-y-2.5">
      {all.map((t, idx) => (
        <Row key={t.key} rank={idx + 1} tally={t} owner={OWNER_BY_KEY.get(t.key)} />
      ))}
    </div>
  );
}

function Row({ rank, tally, owner }: { rank: number; tally: InitiativeTally; owner?: string }) {
  const { medals, rated, involvement } = tally;
  const aware = involvement.directlyInvolved + involvement.aware;
  const segments: { k: keyof typeof medals; v: number }[] = [
    { k: "gold", v: medals.gold },
    { k: "silver", v: medals.silver },
    { k: "bronze", v: medals.bronze },
    { k: "didntLand", v: medals.didntLand },
  ];

  return (
    <div className="rounded-xl border border-parchment-300 bg-parchment-100 px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-[11px] tabular-nums text-ink-500 w-5 shrink-0">{rank}</span>
          <span className="font-display text-base text-ink-900 truncate">{tally.label}</span>
          {tally.isCustom && (
            <span className="text-[9px] uppercase tracking-[0.15em] text-accent-600 shrink-0">added</span>
          )}
          {owner && <span className="text-[10px] uppercase tracking-[0.14em] text-ink-500 shrink-0">{owner}</span>}
        </div>
        <span className="text-[11px] text-ink-500 shrink-0">
          {involvement.directlyInvolved} involved · {aware} aware
        </span>
      </div>

      {rated === 0 ? (
        <p className="mt-2 text-[11px] text-ink-500 italic">No ratings yet</p>
      ) : (
        <div className="mt-2.5 flex items-center gap-3">
          <div className="flex h-2 flex-1 overflow-hidden rounded-full bg-parchment-200">
            {segments.map((s) =>
              s.v > 0 ? (
                <div
                  key={s.k}
                  className="transition-[width] duration-700 ease-out"
                  style={{ width: `${(s.v / rated) * 100}%`, backgroundColor: MEDAL_COLORS[s.k] }}
                  title={`${s.k}: ${s.v}`}
                />
              ) : null,
            )}
          </div>
          <div className="flex items-center gap-2.5 text-[11px] tabular-nums text-ink-500 shrink-0">
            {medals.gold > 0 && <Dot color={MEDAL_COLORS.gold} label={medals.gold} />}
            {medals.silver > 0 && <Dot color={MEDAL_COLORS.silver} label={medals.silver} />}
            {medals.bronze > 0 && <Dot color={MEDAL_COLORS.bronze} label={medals.bronze} />}
            {medals.didntLand > 0 && <Dot color={MEDAL_COLORS.didntLand} label={medals.didntLand} />}
          </div>
        </div>
      )}
    </div>
  );
}

function Dot({ color, label }: { color: string; label: number }) {
  return (
    <span className="flex items-center gap-1">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}
